import { type Types, mapResponsiveStyles } from '@fiverr-private/theme';
import type { TabContextType, TabInternalProps, TabProps, Size } from '../types';
import { isSegmentedControlVariant } from '../helpers';

const STYLES_BY_SIZE: {
    [key in Size]: {
        fontSize: Types.FontSize;
        lineHeight: Types.LineHeight;
        paddingXDefault: Types.Spacing;
        paddingXSegmented: Types.Spacing;
    };
} = {
    sm: { fontSize: 'b_sm', lineHeight: 'b_sm', paddingXDefault: '4', paddingXSegmented: '4' },
    md: { fontSize: 'b_md', lineHeight: 'b_md', paddingXDefault: '8', paddingXSegmented: '6' },
};

const getConditionalStyle = <T>(size: TabContextType['size'], field: string): T =>
    typeof size === 'object'
        ? mapResponsiveStyles(size, (value) => STYLES_BY_SIZE[value][field])
        : STYLES_BY_SIZE[size][field];

export const getStylingProps = (
    variant: TabContextType['variant'],
    placement: TabContextType['placement'],
    isLink: TabProps['isLink'],
    isDisabled: TabProps['isDisabled'],
    isStretched: TabContextType['isStretched'],
    isSelected: TabInternalProps['isSelected'],
    size: TabContextType['size']
): Types.BoxType => ({
    boxSizing: 'borderBox',
    ...(isSegmentedControlVariant(variant) && {
        backgroundColor: !isSelected && !isDisabled ? { hover: 'grey_400' } : 'transparent',
        borderRadius: variant === 'segmentedControlRounded' ? 'circle' : 'sm',
    }),
    fontSize: getConditionalStyle<Types.FontSize>(size, 'fontSize'),
    lineHeight: getConditionalStyle<Types.LineHeight>(size, 'lineHeight'),
    ...(variant === 'default' && {
        borderColor: 'border',
        borderStyle: 'solid',
        ...(placement === 'top' && {
            borderBottomWidth: 'sm',
        }),
        ...(placement === 'bottom' && {
            borderTopWidth: 'sm',
        }),
        ...(placement === 'right' && {
            borderLeftWidth: 'sm',
        }),
        ...(placement === 'left' && {
            borderRightWidth: 'sm',
        }),
    }),
    ...(variant !== 'unstyled' && {
        position: 'relative',
        fontWeight: 'semibold',
        color: isSelected ? 'grey_1200' : { default: 'grey_800', hover: isDisabled ? undefined : 'grey_1200' },
        whiteSpace: 'nowrap',
        userSelect: isDisabled ? 'none' : undefined,
        flex: isStretched ? '1' : undefined,
        ...(!isLink && { paddingY: '2' }),

        ...((placement === 'top' || placement === 'bottom') && {
            ...(isSegmentedControlVariant(variant) && {
                paddingX: getConditionalStyle<Types.Spacing>(size, 'paddingXSegmented'),
            }),
            ...(variant === 'default' && {
                paddingX: getConditionalStyle<Types.Spacing>(size, 'paddingXDefault'),
            }),
            ...(isLink && {
                paddingX: '0',
            }),
            textAlign: 'center',
        }),
        ...(placement === 'left' && {
            paddingX: isLink ? '0' : '4',
            textAlign: 'end',
        }),
        ...(placement === 'right' && {
            paddingX: isLink ? '0' : '4',
            textAlign: 'start',
        }),
    }),
});
